import { v4 } from "uuid";
import {
    Button,
    Form,
    Input,
    Space,
    Row,
    Col,
    Checkbox,
    Card,
    Typography,
    Empty,
    Tooltip,
    Badge,
    Divider,
} from "antd";
import {
    DeleteOutlined,
    PlusOutlined,
    CopyOutlined,
    MailOutlined,
    IdcardOutlined,
    InfoCircleOutlined,
    SaveOutlined,
    MinusOutlined,
    PlusOutlined as ExpandOutlined,
} from "@ant-design/icons";
import { IInstitution, InstitutionClient } from "../../redux/reducer/institution";
import Editor from "../molecules/Editor";
import { defaultEmailJsonContentTiptap } from "../../utils";
import { useState, useEffect, CSSProperties } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../configureStore";

const EMPTY_CLIENTS: InstitutionClient[] = [];

const { Title, Text } = Typography;

const styles: Record<string, CSSProperties> = {
    container: {
        maxHeight: '70vh',
        overflowY: 'auto',
        padding: '4px',
    },
    clientCard: {
        marginBottom: '16px',
        borderRadius: '8px',
        overflow: 'hidden',
        transition: 'all 0.3s',
    },
    clientCardHover: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    },
    clientHeader: {
        padding: '16px 24px',
        backgroundColor: '#fafafa',
        borderBottom: '1px solid #f0f0f0',
    },
    clientBody: {
        padding: '24px',
    },
    formSection: {
        marginBottom: '24px',
    },
    sectionTitle: {
        marginBottom: '12px',
        fontSize: '16px',
        fontWeight: 500,
    },
    addButton: {
        marginBottom: '24px',
        height: '48px',
        borderRadius: '8px',
    },
    actionButtons: {
        marginTop: '24px',
        paddingTop: '24px',
        borderTop: '1px solid #f0f0f0',
    },
};

interface ClientModalParams {
    institution: IInstitution;
    onSubmit?: (values: any) => void;
    onCancel?: () => void;
}

const ClientModal = ({
    institution,
    onSubmit,
    onCancel,
}: ClientModalParams) => {
    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['institution/POST_INSTITUTION_CLIENTS']);
    const clients = useSelector<RootState, InstitutionClient[]>(({ institution: { clients: c } }) => c[institution.institutionId] ?? EMPTY_CLIENTS);
    const [form] = Form.useForm<{ clients: InstitutionClient[] }>();
    const [contentEnabled, setContentEnabled] = useState<{ [key: number]: boolean }>({});
    const [editing, setEditing] = useState<boolean>(false);

    useEffect(() => {
        // Initialize content enabled state for existing clients with content
        const initialState: { [key: number]: boolean } = {};
        clients.forEach((client, index) => {
            if (client.whiteLabelEmail?.enabled && Object.keys(client.whiteLabelEmail?.content).length > 0) {
                initialState[index] = true;
            }
        });
        setContentEnabled(initialState);
    }, [clients]);

    const [expandedCards, setExpandedCards] = useState<number[]>([]);
    const [hoveredCard, setHoveredCard] = useState<number | null>(null);
    const [minimizedCards, setMinimizedCards] = useState<number[]>([]);

    const toggleCard = (index: number) => {
        setExpandedCards(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const toggleMinimize = (index: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setMinimizedCards(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
        // If minimizing, also collapse the card
        if (!minimizedCards.includes(index)) {
            setExpandedCards(prev => prev.filter(i => i !== index));
        }
    };

    return (
        <Form
            form={form}
            initialValues={{ clients }}
            onFinish={onSubmit}
            layout="vertical"
        >
            <div style={styles.container}>
                <Form.List name='clients'>
                    {(fields, { add, remove }) => (
                        <>
                            <Button
                                type="dashed"
                                onClick={() => {
                                    const newIndex = fields.length;
                                    add({
                                        id: '',
                                        name: '',
                                        whiteLabelEmail: {
                                            content: {
                                                json: defaultEmailJsonContentTiptap,
                                                html: undefined
                                            },
                                            enabled: false
                                        }
                                    });
                                    setExpandedCards([...expandedCards, newIndex]);
                                }}
                                icon={<PlusOutlined />}
                                style={styles.addButton}
                                block
                            >
                                Add New Client
                            </Button>

                            {fields.length === 0 ? (
                                <Empty
                                    description="No clients configured"
                                    style={{ padding: '40px 0' }}
                                />
                            ) : (
                                fields.map(({ key, name, ...restField }) => {
                                    const isExpanded = expandedCards.includes(name);
                                    const isHovered = hoveredCard === name;
                                    const isMinimized = minimizedCards.includes(name);
                                    const clientName = form.getFieldValue(['clients', name, 'name']);
                                    const clientId = form.getFieldValue(['clients', name, 'id']);

                                    return (
                                        <Card
                                            key={key}
                                            style={{
                                                ...styles.clientCard,
                                                ...(isHovered ? styles.clientCardHover : {}),
                                            }}
                                            onMouseEnter={() => setHoveredCard(name)}
                                            onMouseLeave={() => setHoveredCard(null)}
                                            styles={{ body: { padding: 0 } }}
                                        >
                                            <div
                                                style={styles.clientHeader}
                                                onClick={() => !isMinimized && toggleCard(name)}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <Row justify="space-between" align="middle">
                                                    <Col>
                                                        <Space size="large">
                                                            <IdcardOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                                                            <div>
                                                                <Title level={5} style={{ margin: 0 }}>
                                                                    {clientName || `Client ${name + 1}`}
                                                                </Title>
                                                                {(!isMinimized && clientId) && (
                                                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                                                        ID: {clientId}
                                                                    </Text>
                                                                )}
                                                            </div>
                                                        </Space>
                                                    </Col>
                                                    <Col>
                                                        <Space>
                                                            {!isMinimized && (
                                                                <Badge
                                                                    status={isExpanded ? "processing" : "default"}
                                                                    text={isExpanded ? "Expanded" : "Collapsed"}
                                                                />
                                                            )}
                                                            <Tooltip title={isMinimized ? "Expand" : "Minimize"}>
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={isMinimized ? <ExpandOutlined /> : <MinusOutlined />}
                                                                    onClick={(e) => toggleMinimize(name, e)}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip title="Remove client">
                                                                <Button
                                                                    type="text"
                                                                    danger
                                                                    icon={<DeleteOutlined />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        remove(name);
                                                                        setExpandedCards(prev => prev.filter(i => i !== name));
                                                                        setMinimizedCards(prev => prev.filter(i => i !== name));
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        </Space>
                                                    </Col>
                                                </Row>
                                            </div>

                                            {isExpanded && !isMinimized && (
                                                <div style={styles.clientBody}>
                                                    {/* Basic Information Section */}
                                                    <div style={styles.formSection}>
                                                        <Title level={5} style={styles.sectionTitle}>
                                                            <IdcardOutlined /> Basic Information
                                                        </Title>
                                                        <Row gutter={16}>
                                                            <Col span={12}>
                                                                <Form.Item
                                                                    {...restField}
                                                                    name={[name, 'id']}
                                                                    label="Client ID"
                                                                    rules={[{ required: true, message: 'Please input client ID' }]}
                                                                    tooltip="Unique identifier for this client"
                                                                >
                                                                    <Input
                                                                        placeholder="Enter client ID"
                                                                        prefix={<IdcardOutlined />}
                                                                        addonAfter={(
                                                                            <Tooltip title={clientId ? "Copy to clipboard" : "Generate UUID"}>
                                                                                <Button
                                                                                    type="text"
                                                                                    size="small"
                                                                                    icon={<CopyOutlined />}
                                                                                    onClick={() => {
                                                                                        if (clientId) {
                                                                                            navigator.clipboard.writeText(clientId);
                                                                                        } else {
                                                                                            const newUuid = v4();
                                                                                            form.setFieldValue(['clients', name, 'id'], newUuid);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {clientId ? 'Copy' : 'Generate'}
                                                                                </Button>
                                                                            </Tooltip>
                                                                        )}
                                                                    />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col span={12}>
                                                                <Form.Item
                                                                    {...restField}
                                                                    name={[name, 'name']}
                                                                    label="Client Name"
                                                                    rules={[{ required: true, message: 'Please input client name' }]}
                                                                    tooltip="Display name for this client"
                                                                >
                                                                    <Input placeholder="Enter client name" />
                                                                </Form.Item>
                                                            </Col>
                                                        </Row>
                                                    </div>

                                                    <Divider />

                                                    {/* Email Configuration Section */}
                                                    <div style={styles.formSection}>
                                                        <Title level={5} style={styles.sectionTitle}>
                                                            <MailOutlined /> Email Configuration
                                                        </Title>
                                                        <Form.Item
                                                            name={[name, 'whiteLabelEmail', 'enabled']}
                                                            valuePropName="checked"
                                                        >
                                                            <Checkbox
                                                                checked={contentEnabled[name] ?? false}
                                                                onChange={(e) => {
                                                                    setContentEnabled({
                                                                        ...contentEnabled,
                                                                        [name]: e.target.checked
                                                                    });
                                                                }}
                                                            >
                                                                <Space>
                                                                    <span>Enable White-label Email</span>
                                                                    <Tooltip title="Customize invitation emails for this specific client">
                                                                        <InfoCircleOutlined style={{ color: '#999' }} />
                                                                    </Tooltip>
                                                                </Space>
                                                            </Checkbox>
                                                        </Form.Item>

                                                        {contentEnabled[name] && (
                                                            <Card style={{ marginTop: '16px', backgroundColor: '#fafafa' }}>
                                                                <Form.Item
                                                                    {...restField}
                                                                    name={[name, 'whiteLabelEmail', 'content']}
                                                                    label="Email Template"
                                                                >
                                                                    <Editor
                                                                        content={form.getFieldValue(['clients', name, 'whiteLabelEmail', 'content', 'json']) || defaultEmailJsonContentTiptap}
                                                                        editingChange={(isEdit) => { setEditing(isEdit); }}
                                                                        onSave={async (jsonContent, htmlContent) => {
                                                                            const clients: InstitutionClient[] = form.getFieldValue('clients');
                                                                            clients[name].whiteLabelEmail.content = {
                                                                                json: jsonContent,
                                                                                html: htmlContent ?? '',
                                                                            };
                                                                            form.setFieldsValue({ clients });
                                                                        }}
                                                                        title={""}
                                                                        type="email"
                                                                    />
                                                                </Form.Item>
                                                            </Card>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </Card>
                                    );
                                })
                            )}
                        </>
                    )}
                </Form.List>
            </div>

            <div style={styles.actionButtons}>
                <Row justify="end" gutter={16}>
                    <Col>
                        <Button
                            size="large"
                            disabled={loading}
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                    </Col>
                    <Col>
                        <Button
                            size="large"
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            disabled={editing}
                            icon={<SaveOutlined />}
                        >
                            Save Clients
                        </Button>
                    </Col>
                </Row>
            </div>
        </Form>
    );
};

export default ClientModal;
